import * as React from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { DataGrid, type GridColDef, type GridEventListener, type GridPaginationModel } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import PageContainer from './PageContainer';
import {
  getAttendanceRecords,
  getEmployeeAttendanceList,
  type AttendanceRecord,
  type EmployeeAttendance,
} from '../data/attendance';
import type { Employee } from '../data/employees';
import CustomTablePagination from './CustomTablePagination';
import DateRangeFilter from './DateRangeFilter';

const DEFAULT_PAGINATION: GridPaginationModel = { page: 0, pageSize: 25 };

// The employee view starts one month back from today.
const defaultDateFrom = () => dayjs().subtract(1, 'month').format('YYYY-MM-DD');
const defaultDateTo = () => dayjs().format('YYYY-MM-DD');

export default function AttendanceList() {
  const { t, language, direction } = useLanguage();
  const { user } = useAuth();
  const isRtl = direction === 'rtl';

  // Branch Managers only ever see their own branch.
  const branchId = user?.role === 'BM' ? user.branch_id : undefined;

  const [attendanceData, setAttendanceData] = React.useState<EmployeeAttendance[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>(DEFAULT_PAGINATION);

  // Employee drill-down: set when a row of the daily list is clicked.
  const [selectedEmployee, setSelectedEmployee] = React.useState<Employee | null>(null);
  const [employeeRecords, setEmployeeRecords] = React.useState<AttendanceRecord[]>([]);
  const [isEmployeeLoading, setIsEmployeeLoading] = React.useState(false);
  const [employeeError, setEmployeeError] = React.useState<Error | null>(null);
  const [employeePaginationModel, setEmployeePaginationModel] =
    React.useState<GridPaginationModel>(DEFAULT_PAGINATION);
  const [dateFrom, setDateFrom] = React.useState<string | null>(defaultDateFrom);
  const [dateTo, setDateTo] = React.useState<string | null>(defaultDateTo);

  const loadAttendance = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getEmployeeAttendanceList(branchId);
      setAttendanceData(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [branchId]);

  React.useEffect(() => {
    loadAttendance();
  }, [loadAttendance]);

  const loadEmployeeAttendance = React.useCallback(async () => {
    if (!selectedEmployee) {
      return;
    }

    setIsEmployeeLoading(true);
    setEmployeeError(null);
    try {
      const records = await getAttendanceRecords({
        userId: selectedEmployee.id,
        branchId,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      setEmployeeRecords(records);
    } catch (err) {
      setEmployeeError(err as Error);
    } finally {
      setIsEmployeeLoading(false);
    }
  }, [selectedEmployee, branchId, dateFrom, dateTo]);

  React.useEffect(() => {
    loadEmployeeAttendance();
  }, [loadEmployeeAttendance]);

  const handleRowClick = React.useCallback<GridEventListener<'rowClick'>>(({ row }) => {
    setSelectedEmployee((row as EmployeeAttendance).employee);
    setEmployeeRecords([]);
    setEmployeePaginationModel(DEFAULT_PAGINATION);
    setDateFrom(defaultDateFrom());
    setDateTo(defaultDateTo());
  }, []);

  const handleBackToDaily = React.useCallback(() => {
    setSelectedEmployee(null);
    setEmployeeRecords([]);
    setEmployeeError(null);
  }, []);

  const handleRefresh = React.useCallback(() => {
    if (selectedEmployee) {
      loadEmployeeAttendance();
    } else {
      loadAttendance();
    }
  }, [selectedEmployee, loadEmployeeAttendance, loadAttendance]);

  const formatDateTime = React.useCallback(
    (dateTime: string | null) => {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return date.toLocaleString(language === 'ar' ? 'ar-EG' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    },
    [language],
  );

  const getStatusChip = React.useCallback((status: 'absent' | 'inOffice' | 'left') => {
    const statusConfig = {
      absent: {
        label: t('attendance.absent'),
        color: 'error' as const,
      },
      inOffice: {
        label: t('attendance.inOffice'),
        color: 'success' as const,
      },
      left: {
        label: t('attendance.left'),
        color: 'default' as const,
      },
    };

    const config = statusConfig[status];
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        sx={{ fontWeight: 500 }}
      />
    );
  }, [t]);

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: 'employeeName',
        headerName: t('attendance.employeeName'),
        flex: 1,
        minWidth: 150,
        align: isRtl ? 'right' : 'left',
        headerAlign: 'left',
        valueGetter: (value, row: EmployeeAttendance) => row.employee.name,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'status',
        headerName: t('attendance.status'),
        width: 130,
        align: isRtl ? 'right' : 'left',
        headerAlign: 'left',
        renderCell: (params) => getStatusChip(params.row.status),
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'checkIn',
        headerName: t('attendance.checkIn'),
        width: 150,
        align: isRtl ? 'right' : 'left',
        headerAlign: 'left',
        valueGetter: (value, row: EmployeeAttendance) =>
          formatDateTime(row.attendance?.check_in_at || null),
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'checkOut',
        headerName: t('attendance.checkOut'),
        width: 150,
        align: isRtl ? 'right' : 'left',
        headerAlign: 'left',
        valueGetter: (value, row: EmployeeAttendance) =>
          formatDateTime(row.attendance?.check_out_at || null),
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
    ],
    [t, isRtl, formatDateTime, getStatusChip]
  );

  const employeeColumns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: 'date',
        headerName: t('attendance.date'),
        flex: 1,
        minWidth: 130,
        align: isRtl ? 'right' : 'left',
        headerAlign: 'left',
        valueGetter: (value, row: AttendanceRecord) =>
          row.date ? dayjs(row.date).format('YYYY-MM-DD') : '-',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'checkIn',
        headerName: t('attendance.checkIn'),
        width: 150,
        align: isRtl ? 'right' : 'left',
        headerAlign: 'left',
        valueGetter: (value, row: AttendanceRecord) => formatDateTime(row.check_in_at),
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'checkOut',
        headerName: t('attendance.checkOut'),
        width: 150,
        align: isRtl ? 'right' : 'left',
        headerAlign: 'left',
        valueGetter: (value, row: AttendanceRecord) => formatDateTime(row.check_out_at),
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
    ],
    [t, isRtl, formatDateTime]
  );

  const rows = React.useMemo(
    () =>
      attendanceData.map((item) => ({
        id: item.employee.id,
        ...item,
      })),
    [attendanceData]
  );

  const employeeRows = React.useMemo(
    () =>
      employeeRecords.map((record, index) => ({
        ...record,
        id: record.id ?? index,
      })),
    [employeeRecords]
  );

  const activeError = selectedEmployee ? employeeError : error;

  const refreshAction = (
    <IconButton
      onClick={handleRefresh}
      disabled={isLoading || isEmployeeLoading}
      aria-label="refresh"
      sx={{
        color: '#028bcb',
        '&:hover': {
          backgroundColor: 'rgba(2, 139, 203, 0.08)',
        },
      }}
    >
      <RefreshIcon />
    </IconButton>
  );

  const backButton = selectedEmployee ? (
    <IconButton
      onClick={handleBackToDaily}
      aria-label="back"
      sx={(theme) => ({
        backgroundColor: 'transparent',
        borderRadius: '50%',
        '&:hover': {
          backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : '#31394a',
          color: theme.palette.mode === 'dark'
            ? theme.palette.text.primary
            : 'white',
        },
      })}
    >
      {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
    </IconButton>
  ) : null;

  if (activeError) {
    return (
      <PageContainer
        title={selectedEmployee ? t('attendance.employeeAttendance') : t('attendance.title')}
        backButton={backButton}
      >
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{activeError.message}</Alert>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={selectedEmployee ? t('attendance.employeeAttendance') : t('attendance.title')}
      backButton={backButton}
      actions={refreshAction}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {selectedEmployee ? (
          <React.Fragment>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mb: 2,
                px: 1,
              }}
            >
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {selectedEmployee.name}
              </Typography>
              <DateRangeFilter
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
              />
            </Box>
            <DataGrid
              rows={employeeRows}
              columns={employeeColumns}
              loading={isEmployeeLoading}
              disableRowSelectionOnClick
              disableColumnFilter
              disableColumnMenu
              disableColumnResize
              paginationModel={employeePaginationModel}
              onPaginationModelChange={setEmployeePaginationModel}
              pageSizeOptions={[25, 50, 100]}
              hideFooter
              localeText={{
                noRowsLabel: t('attendance.noAttendance'),
              }}
              sx={{
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(2, 139, 203, 0.05)',
                },
              }}
            />
            <CustomTablePagination
              paginationModel={employeePaginationModel}
              rowCount={employeeRows.length}
              onPaginationModelChange={setEmployeePaginationModel}
              pageSizeOptions={[25, 50, 100]}
            />
          </React.Fragment>
        ) : (
          <React.Fragment>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={isLoading}
              onRowClick={handleRowClick}
              disableRowSelectionOnClick
              disableColumnFilter
              disableColumnMenu
              disableColumnResize
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[25, 50, 100]}
              hideFooter
              localeText={{
                noRowsLabel: t('attendance.noAttendance'),
              }}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(2, 139, 203, 0.05)',
                },
              }}
            />
            <CustomTablePagination
              paginationModel={paginationModel}
              rowCount={rows.length}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[25, 50, 100]}
            />
          </React.Fragment>
        )}
      </Box>
    </PageContainer>
  );
}
